<template>
  <v-layout column>
    <v-dialog
      max-width="1000px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="previewDialogOpened"
      @keydown.esc="
        previewDialogOpened = false;
        documentPreviewData = '';
        previewRotation = 0;
      "
    >
      <v-card v-if="selectedDocumentLink && previewType">
        <v-card-title class="title">
          <span class="text-truncate" style="max-width: 600px">{{
            getDocumentName(selectedDocumentLink)
          }}</span>

          <v-spacer />
          <v-tooltip bottom class="mr-1">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="previewRotation += 90"
              slot="activator"
            >
              <v-icon size="25"> rotate_right </v-icon>
            </v-btn>
            <span>Rotate</span>
          </v-tooltip>
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0 ml-1"
            @click="
              previewDialogOpened = false;
              documentPreviewData = '';
              previewRotation = 0;
            "
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="documentPreviewData && previewLoaded">
          <preview-document
            :document-preview-data="documentPreviewData"
            :preview-type="previewType"
            :preview-rotation="previewRotation"
          />
        </v-card-text>
        <v-card-text v-else>
          <v-progress-circular
            indeterminate
            size="50"
            class="primary--text ma-5"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      max-width="800px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      persistent
      v-model="selectOptionsDialogOpened"
    >
      <v-card v-if="documentUploading">
        <v-card-text>
          <v-layout column align-center justify-center class="ma-4">
            <p class="subheading">Uploading documents. Please wait...</p>
            <v-progress-circular
              indeterminate
              size="50"
              class="primary--text"
            />
          </v-layout>
        </v-card-text>
      </v-card>

      <v-card v-else-if="currentDocument">
        <v-card-title>
          <h2>Tell us about the document you just uploaded</h2>
        </v-card-title>

        <v-card-text class="text-xs-left pt-0">
          <p class="subheading">
            What is <b>{{ currentDocument.data.name }}</b> about? Please select
            all that apply.
          </p>

          <v-layout
            column
            v-for="question in allDocumentsNeeded"
            :key="question.id"
            class="option-group"
            v-if="question.options.length > 0"
          >
            <v-checkbox
              v-for="option in question.options"
              v-model="selectedOptions"
              :key="option"
              :label="option"
              :value="option"
              small
              solo
              hide-details
            />
          </v-layout>

          <v-layout column class="option-group">
            <v-checkbox
              v-model="selectedOtherOptions"
              label="Others"
              small
              solo
              hide-details
              class="mb-1"
            />

            <v-layout
              v-if="selectedOtherOptions"
              v-for="(option, index) in otherOptions"
              :key="index"
              align-center
              class="mt-2"
            >
              <v-text-field
                class="mx-4 my-0 default-input"
                label="Enter document name"
                v-model="otherOptions[index]"
                solo
                hide-details
              />

              <v-btn
                flat
                icon
                color="error"
                class="my-0"
                @click="deleteOtherOption(index)"
              >
                <v-icon class="material-icons-outlined">delete</v-icon>
              </v-btn>
            </v-layout>

            <v-layout v-if="selectedOtherOptions" class="mt-1">
              <v-btn
                outline
                color="primary"
                small
                class="mx-4 text-none"
                @click="addMoreOptions()"
              >
                <v-icon>add</v-icon>
                Add more row
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            class="error--text text-none"
            flat
            @click="cancelSelectOptions()"
          >
            Cancel
          </v-btn>

          <v-btn
            class="primary text-none"
            @click="eventEmitter.emit('selected-options')"
            :disabled="!canInsert"
          >
            Done
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmationDialog
      v-model="deleteAllDocumentDialogOpened"
      @confirm="deleteQuestionDocuments()"
      @cancel="closedDeleteDocumentsConfirmationDialog()"
    >
      {{ $t('questionnaire.removeAllUploadedDocuments') }}
    </ConfirmationDialog>

    <input
      type="file"
      style="display: none"
      ref="selectedDocuments"
      multiple
      @change="onDocumentsPicked"
    />

    <v-layout column align-center v-if="checklistLoading">
      <v-progress-circular
        indeterminate
        size="40"
        class="ma-5"
        color="primary"
      ></v-progress-circular>
    </v-layout>

    <v-layout column v-else class="mb-2 subheading mt-2">
      <v-layout class="text-xs-left body-1">
        <span
          >If you prefer to <b>upload documents in bulk</b> and would like us to
          handle the documents for you, please upload them in Bulk Upload
          section below.</span
        >
      </v-layout>

      <h2 class="section-title text-xs-left mt-3 mb-2" v-if="!hideList">
        Document checklist
      </h2>

      <span
        v-if="isEmpty(allDocumentsNeeded) && !hideList"
        class="darkgrey--text body-2 text-xs-left"
        >Documents were not requested for the questionnaire.</span
      >

      <v-expansion-panel class="my-2 expansion-panel" expand v-model="expanded">
        <v-expansion-panel-content
          v-for="question in allDocumentsNeeded"
          :key="question.id"
          :class="
            showUnanswered && isEmpty(question.answer) ? 'outlined-red' : ''
          "
          :value="showUnanswered && isEmpty(question.answer) ? true : undefined"
        >
          <template v-slot:header>
            <v-layout row>
              <div class="pr-2">
                <v-icon
                  v-if="question.answer_by"
                  large
                  class="darkgrey--text text--lighten-2"
                >
                  watch_later
                </v-icon>

                <v-icon
                  v-else-if="!isEmpty(question.answer)"
                  large
                  color="success"
                >
                  check_circle
                </v-icon>

                <v-icon v-else large class="darkgrey--text text--lighten-2"
                  >radio_button_unchecked</v-icon
                >
              </div>

              <div
                class="document-item-text"
                v-html="
                  !isEmpty(question.options)
                    ? question.options.join(', ')
                    : question.text
                "
              />
            </v-layout>
          </template>

          <v-card>
            <v-card-text class="pt-0">
              <v-layout column class="px-2">
                <v-layout align-center v-if="question.answer_by">
                  <span class="darkgrey--text text-xs-left subheading">
                    {{
                      $t(
                        currentQuestionnaire.batch_reminder
                          ? 'questionnaireQuestion.weWillAskYouToUploadAroundDate'
                          : 'questionnaireQuestion.weWillAskYouToUploadAfterDate',
                        {
                          date: moment(question.answer_by).format('ll'),
                        },
                      )
                    }}
                  </span>
                  <v-btn
                    @click="saveQuestion(question, '')"
                    outline
                    small
                    color="primary"
                    class="ml-3 font-weight-bold text-capitalize"
                    >Undo</v-btn
                  >
                </v-layout>

                <v-layout
                  align-center
                  v-else-if="question.answer === 'Not Applicable'"
                >
                  <span class="darkgrey--text text-xs-left subheading"
                    >You marked this question as
                    <strong>not applicable</strong> to you.</span
                  >
                  <v-btn
                    @click="saveQuestion(question, '')"
                    outline
                    small
                    color="primary"
                    class="ml-3 font-weight-bold text-capitalize"
                    >Undo</v-btn
                  >
                </v-layout>

                <v-layout
                  align-center
                  v-else-if="question.answer === 'Marked Completed'"
                >
                  <span class="darkgrey--text text-xs-left subheading"
                    >This was marked completed by {{ currentFirm.name }}. You
                    can skip this question.</span
                  >
                </v-layout>

                <v-layout
                  align-center
                  v-else-if="question.answer === 'Provided in Questionnaire'"
                >
                  <span class="darkgrey--text text-xs-left subheading"
                    >You have marked this provided in the questionnaire.</span
                  >
                  <v-btn
                    @click="saveQuestion(question, '')"
                    outline
                    small
                    color="primary"
                    class="ml-3 font-weight-bold text-capitalize"
                    >Undo</v-btn
                  >
                </v-layout>

                <v-layout
                  align-center
                  v-else-if="question.answer === 'Provided Elsewhere'"
                >
                  <span class="darkgrey--text text-xs-left subheading"
                    >You have marked this provided elsewhere.</span
                  >
                  <v-btn
                    @click="saveQuestion(question, '')"
                    outline
                    small
                    color="primary"
                    class="ml-3 font-weight-bold text-capitalize"
                    >Undo</v-btn
                  >
                </v-layout>

                <div v-else class="description mb-2 darkgrey--text">
                  <div
                    v-if="
                      question.section === 'Documents from Last Year' ||
                        question.section === 'Other Prior Year Documents'
                    "
                  >
                    <span
                      >You uploaded the same document last year. If you don't
                      have it this year, please select "Not Applicable".</span
                    >
                  </div>
                  <div v-else-if="question.follow_up_question_id">
                    <span
                      v-html="
                        formatReason(
                          question,
                          getParentQuestion(findSection(question), question),
                        )
                      "
                    />
                  </div>
                  <div v-else-if="filteredComments(question).length">
                    <v-chip
                      class="clickable-chip"
                      small
                      @click="$emit('open-comment-dialog', question)"
                      >{{ filteredComments(question).length }} Note{{
                        filteredComments(question).length > 1 ? 's' : ''
                      }}</v-chip
                    >
                    <span>Click to view notes</span>
                  </div>
                </div>
              </v-layout>

              <v-layout
                v-if="!isEmpty(question.answer)"
                align-center
                class="file-item mx-2 mb-2 px-3 py-2 text-xs-left body-1"
                v-for="(document, i) in listDocuments(question.answer)"
                :key="document.id"
                @click="previewDocument(document)"
              >
                <v-icon
                  color="grey darken-2"
                  class="mr-1 material-icons-outlined"
                >
                  insert_drive_file
                </v-icon>
                {{ getDocumentName(document) }}
                <v-spacer />
                <v-btn
                  @click.stop.prevent="deleteDocument(question, document)"
                  style="cursor: pointer"
                  color="error"
                  flat
                  small
                  class="text-none"
                >
                  Remove
                </v-btn>
              </v-layout>

              <v-layout
                wrap
                class="pt-2"
                v-if="
                  question.answer !== 'Upload Later' &&
                    question.answer !== 'Answer Later' &&
                    question.answer !== 'Not Applicable' &&
                    question.answer !== 'Provided Elsewhere'
                "
              >
                <v-btn
                  color="primary"
                  class="text-none"
                  @click="$emit('upload', question)"
                  :loading="uploadingDocs.includes(question.id)"
                >
                  Upload
                </v-btn>

                <v-btn
                  v-if="!question.disable_not_applicable"
                  outline
                  color="primary"
                  class="text-none"
                  @click="
                    checkIfOpenDeleteDocumentConfirmationDialog(question)
                      ? openDeleteDocumentsConfirmationDialog(
                          question,
                          'Not Applicable',
                        )
                      : saveQuestion(question, 'Not Applicable')
                  "
                >
                  Not Applicable
                </v-btn>

                <v-btn
                  v-if="!question.disable_answer_later"
                  outline
                  color="primary"
                  class="text-none"
                  @click="$emit('open-answer-later-dialog', question, true)"
                >
                  Upload Later
                </v-btn>

                <v-btn
                  v-if="!disableProvidedElsewhere"
                  outline
                  color="primary"
                  class="text-none"
                  @click="
                    checkIfOpenDeleteDocumentConfirmationDialog(question)
                      ? openDeleteDocumentsConfirmationDialog(
                          question,
                          'Provided Elsewhere',
                        )
                      : saveQuestion(question, 'Provided Elsewhere')
                  "
                >
                  Provided Elsewhere
                </v-btn>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>

        <v-expansion-panel-content :value="otherDocumentsTab">
          <template v-slot:header>
            <v-layout row align-center>
              <div class="pr-2">
                <v-icon
                  large
                  class="darkgrey--text text--lighten-1 material-icons-outlined"
                  >file_copy</v-icon
                >
              </div>
              <span class="document-item-text">Additional Attachments</span>
            </v-layout>
          </template>

          <v-card>
            <v-card-text class="pt-0">
              <v-layout
                align-center
                class="file-item mx-2 mb-2 px-3 py-2 text-xs-left body-1"
                v-for="(document, i) in currentQuestionnaireOtherDocuments"
                :key="i"
                @click="previewDocument(document)"
              >
                <v-icon
                  color="grey darken-2"
                  class="mr-1 material-icons-outlined"
                >
                  insert_drive_file
                </v-icon>
                {{ getDocumentName(document) }}
                <v-spacer />

                <v-btn
                  @click.stop.prevent="deleteDocument(null, document)"
                  style="cursor: pointer"
                  color="error"
                  flat
                  small
                  class="text-none"
                >
                  Remove
                </v-btn>
              </v-layout>

              <v-layout wrap class="pt-2">
                <v-btn
                  color="primary"
                  class="text-none"
                  @click="pickDocuments()"
                >
                  Upload
                </v-btn>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Bulk upload -->
      <h2 class="section-title text-xs-left mt-3 mb-2">
        Bulk upload
      </h2>

      <v-layout class="text-xs-left body-1">
        <span
          >For the items that you provide in the bulk file, please click
          <b>Provided Elsewhere</b> in the document checklist above.</span
        >
      </v-layout>

      <v-layout class="mt-3 mb-4" wrap>
        <v-btn
          block
          @click="pickDocuments"
          class="primary hidden-sm-and-up text-none mb-3"
        >
          Choose Files
        </v-btn>

        <v-flex class="hidden-xs-only">
          <v-layout
            class="drag-and-drop"
            column
            align-center
            v-if="bulkUploadProcessorUploading"
          >
            <v-layout column align-center justify-center>
              <v-progress-circular
                indeterminate
                size="40"
                class="ma-5"
                color="primary"
              ></v-progress-circular>
            </v-layout>
          </v-layout>
          <v-layout
            class="drag-and-drop"
            @drop.prevent="addFile"
            @dragover.prevent
            v-else
          >
            <v-layout column align-center justify-center>
              <div>
                <v-icon>upload_file</v-icon>
              </div>
              <div>
                <span @click="pickDocuments" class="drag-and-drop-text"
                  >Drag and drop files here or
                  <span class="clickable primary--text">browse</span></span
                >
              </div>
            </v-layout>
          </v-layout>
        </v-flex>
      </v-layout>

      <v-layout
        align-center
        class="file-item mb-2 py-2 px-3 text-xs-left body-1"
        v-if="bulkUploadProcessor"
        v-for="(document, i) in currentQuestionnaireBulkDocuments"
        :key="document.id"
        @click="previewDocument(formatPreviewLink(document))"
      >
        <v-icon class="mr-1 material-icons-outlined grey--text text--darken-2">
          insert_drive_file
        </v-icon>

        <v-layout column class="pl-1">
          <div class="file-name">
            {{ getDocumentName(document.source) }}
          </div>

          <v-layout>
            <v-icon color="info" small class="mr-1 material-icons-outlined">
              info
            </v-icon>
            <span class="grey--text text--darken-4">In review:</span>
            <span class="grey--text text--darken-2 pl-1"
              >We will review this document and get back to you.</span
            >
          </v-layout>
        </v-layout>

        <v-btn
          flat
          icon
          @click="deleteExtractedDocument(document.id)"
          @click.native.stop
          :loading="deletingDocIds.includes(document.id)"
          color="error"
        >
          <v-icon size="20">delete</v-icon>
        </v-btn>
      </v-layout>
    </v-layout>
  </v-layout>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';
import moment from 'moment';
import PreviewDocument from '@/components/PreviewDocument';
import EventEmitter from 'eventemitter3';
import NewDocumentChecklistMixin from '@/mixins/NewDocumentChecklistMixin';
import FirmPreferenceMixins from '../../mixins/FirmPreferenceMixins';
import QuestionnaireMixin from '@/mixins/QuestionnaireMixin';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import API from '@/api';
export default {
  components: {
    PreviewDocument,
    ConfirmationDialog,
  },

  mixins: [NewDocumentChecklistMixin, QuestionnaireMixin, FirmPreferenceMixins],

  props: {
    currentCompany: {
      type: Object,
    },

    checklistLoading: {
      type: Boolean,
    },

    uploadingDocs: {
      type: Array,
      required: true,
    },

    currentQuestionnaire: {
      type: Object,
      default: null,
    },

    showUnanswered: {
      type: Boolean,
      default: false,
    },

    hideList: {
      optional: true,
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      bulkUploadProcessorUploading: false,
      deletingDocIds: [],
      expanded: [],
      deleteAllDocumentDialogOpened: false,
      currentQuestion: null,
      currentProgress: '',
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
    }),

    currentQuestionnaireBulkDocuments() {
      if (this.currentQuestionnaire.extracted_documents) {
        return this.currentQuestionnaire.extracted_documents
          .filter(doc => {
            return doc.document_type === 'bulk_document';
          })
          .filter(doc => {
            return (
              doc.status === 'processing' || doc.status === 'review_required'
            );
          });
      } else {
        return [];
      }
    },
  },

  methods: {
    isEmpty,
    moment,

    async deleteExtractedDocument(id) {
      try {
        this.deletingDocIds.push(id);
        await API.extractedDocument.deleteExtractedDocument(id);

        this.$emit('refresh-questionnaire');
      } catch (e) {
        console.log(e);
        this.deletingDocIds = this.deletingDocIds.filter(d => d !== id);
      }
    },

    formatPreviewLink(document) {
      return document.source
        .split('/')
        .slice(3)
        .join('/');
    },

    formatReason(question, parentQuestion) {
      return `${this.currentUser.is_advisor ? 'Client' : 'You'} answered <b>'${
        question.trigger_for_follow_up_question
      }</b>' to<br/>'<b>${parentQuestion.text}</b>'`;
    },

    pickDocuments() {
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },

    async addFile(e) {
      const documents = e.dataTransfer.files;

      if (documents) {
        const documentType = this.selectedDocumentType;
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name: this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
            };
          }),
        );

        this.uploadBulkDocument();
      }
    },

    async onDocumentsPicked(e) {
      const documents = e.target.files;
      if (documents) {
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name: this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
            };
          }),
        );

        this.uploadBulkDocument();
      }
    },

    checkIfOpenDeleteDocumentConfirmationDialog(question) {
      if (
        question.answer &&
        !['Not Applicable', 'Provided Elsewhere', ''].includes(question.answer)
      ) {
        return true;
      }
      return false;
    },

    openDeleteDocumentsConfirmationDialog(question, action) {
      this.deleteAllDocumentDialogOpened = true;
      this.currentQuestion = question;
      this.currentProgress = action;
    },

    deleteQuestionDocuments() {
      this.saveQuestion(this.currentQuestion, this.currentProgress);
      this.closedDeleteDocumentsConfirmationDialog();
    },

    closedDeleteDocumentsConfirmationDialog() {
      this.deleteAllDocumentDialogOpened = false;
      this.currentQuestion = null;
      this.currentProgress = '';
    },
  },

  mounted() {
    this.expanded = this.allDocumentsNeeded.map(d => d.id);
  },
};
</script>

<style lang="postcss" scoped>
.section-title {
  font-weight: 700;
  font-size: 20px;
}
.option-group {
  padding: 0px 10px 12px;
  margin: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  & >>> .v-input {
    margin-top: 12px;
  }
  & >>> .default-input .v-input__slot {
    border: 1px solid #c9c9c9;
    border-radius: 3px;
  }
}
.file-item {
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  background: #fff;
  cursor: pointer;

  &:hover {
    background: #f1f1f1;
  }
}

.document-item {
  padding: 5px 0px;
}

.expansion-panel {
  border: none;
  box-shadow: none;

  & >>> .v-expansion-panel__container {
    border: 1px solid rgba(0, 0, 0, 0.2) !important;
    border-radius: 4px;
    margin-bottom: 12px;
    overflow: hidden;
  }

  & >>> .v-expansion-panel__container.outlined-red {
    border: 1px solid red !important;
  }
}
.description {
  font-size: 15px;
}

.document-item-text {
  margin-top: 6px;
  font-size: 16px;

  & >>> p {
    margin-bottom: 8px;
  }
}

.select-option {
  & >>> .v-input__control .v-input__slot {
    min-height: 46px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-raidus: 4px;
    font-size: 15px;
  }
}
.why-text {
  font-size: 12px;
}
.action-button {
  & >>> .v-btn__content {
    white-space: normal;
  }
}
.drag-and-drop {
  height: 140px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);

  & >>> i {
    color: rgba(0, 0, 0, 0.3);
  }

  & >>> span {
    color: rgba(0, 0, 0, 0.6);
  }

  & >>> span.clickable {
    cursor: pointer;
  }
}
.clickable-chip {
  & >>> .v-chip__content {
    cursor: pointer !important;
  }
}
.outlined-red {
  border: 1px solid #cb4039;
}
.drag-and-drop-text {
  font-size: 14px;
}
.max-size-text {
  font-size: 14px;
}
.bulk-item {
  padding: 8px 20px;
  align-items: center;
}

.file-name {
  font-size: 16px;
  font-weight: bold;
}
</style>
